import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Box, Portal, useOutsideClick, Select } from "@chakra-ui/react";
import { Avatar, DynamicTable } from "@/components";
import { WithAuth } from "@/hoc";
import { useSubscription } from "@/hooks";
import { Subscription, SubscriptionPaginator } from "@/models";
import {
  getPeriodicity,
  getStatus,
  formatCurrency,
  getColorBadgeStatus,
  formatDate,
} from "@/utils";
import { ColumnDefinition, PageHeading, Pills, ActionDrawerItems, Icon, IconButton, DrawerList, Search } from "barte-design-system";
import { DotsThreeVerticalIcon, ArrowSquareOutIcon } from "@phosphor-icons/react";
import { SubscriptionTableFilter, SubscriptionFilterState } from "@/components/SubscriptionTableFilter";

interface FetchParams {
  status: string
  startDate: string | null
  endDate: string | null
  search: string
  searchType: string
  page: number
}


const Assinaturas = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getSubscriptions } = useSubscription();
  const [data, setData] = useState({} as SubscriptionPaginator);
  const [loading, setLoading] = useState<boolean>(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [pageSize, setPageSize] = useState(8);
  const [searchValue, setSearchValue] = useState(searchParams.get("search") || "");
  const [filterParams, setFilterParams] = useState(searchParams.get("searchType") || "customerName");

  const currentPage = Math.max(0, Number(searchParams.get("page") || 1) - 1);

  const filterDefaultValues: SubscriptionFilterState = {
    status: searchParams.get("status") || "ALL",
    startDate: searchParams.get("startDate") || null,
    endDate: searchParams.get("endDate") || null,
  };

  const appliedCount = [
    searchParams.get("status") && searchParams.get("status") !== "ALL",
    searchParams.get("startDate") || searchParams.get("endDate"),
  ].filter(Boolean).length;

  async function getSubscriptionsData(params: FetchParams) {
    setLoading(true);
    try {
      const { data } = await getSubscriptions(
        {
          status: params.status === "ALL" ? "" : params.status,
          expirationDateInitial: params.startDate || "",
          expirationDateFinal: params.endDate || "",
          [params.searchType]: params.search || "",
        },
        { size: pageSize, page: params.page, sort: "startDate,DESC" }
      );
      setData(data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  const handleFilterChange = (filterState: SubscriptionFilterState) => {
    const params = new URLSearchParams(searchParams);

    if (filterState.status && filterState.status !== "ALL") {
      params.set("status", filterState.status);
    } else {
      params.delete("status");
    }

    if (filterState.startDate) {
      params.set("startDate", filterState.startDate);
    } else {
      params.delete("startDate");
    }

    if (filterState.endDate) {
      params.set("endDate", filterState.endDate);
    } else {
      params.delete("endDate");
    }

    params.delete("page");
    setSearchParams(params);
  };

  const handleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams);

    if (value) {
      params.set("search", value);
      if (filterParams !== "customerName") {
        params.set("searchType", filterParams);
      } else {
        params.delete("searchType");
      }
    } else {
      params.delete("search");
      params.delete("searchType");
    }

    params.delete("page");
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    if (page > 1) {
      params.set("page", String(page));
    } else {
      params.delete("page");
    }
    setSearchParams(params);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    const params = new URLSearchParams(searchParams);
    params.delete("page");
    setSearchParams(params);
  };

  const columns: ColumnDefinition<Subscription>[] = [
    {
      key: "customerName",
      title: "Cliente",
      dataIndex: "customerName",
    },
    {
      key: "startDate",
      title: "Contratação",
      dataIndex: "startDate",
      render: (startDate) => formatDate(startDate),
    },
    {
      key: "titlePlan",
      title: "Plano",
      dataIndex: "titlePlan",
    },
    {
      key: "type",
      title: "Periodicidade ",
      dataIndex: "basicValue",
      render: (basicValue) => getPeriodicity(basicValue?.type),
    },
    {
      key: "value",
      title: "Valor/mês",
      dataIndex: "basicValue",
      render: (basicValue) => formatCurrency({ value: basicValue?.value }),
    },
    {
      key: "status",
      title: "Status",
      dataIndex: "status",
      render: (status) =>
        status ? (
          <Pills label={getStatus(status)} variant="light" state={getColorBadgeStatus(status)} size="sm" />
        ) : (
          "-"
        ),
    },
    {
      key: "actions",
      title: "",
      dataIndex: "uuid",
      align: "right" as const,
      render: (text, record) => (
        <Box position="relative"
          onClick={(e) => e.stopPropagation()}
        >
          <IconButton
            ref={(el) => {
              if (el) {
                buttonRefs.current.set(record.uuid, el);
              } else {
                buttonRefs.current.delete(record.uuid);
              }
            }}
            variant="outline"
            size="sm"
            round="soft"
            aria-label="Actions"
            onClick={(e) => {
              e.stopPropagation();
              const button = buttonRefs.current.get(record.uuid);
              if (button) {
                const rect = button.getBoundingClientRect();
                setMenuPosition({
                  top: rect.top + window.scrollY,
                  right: window.innerWidth - rect.left + 8
                });
              }
              setOpenMenuId(openMenuId === record.uuid ? null : record.uuid);
            }}
          >
            <DotsThreeVerticalIcon weight="bold" />
          </IconButton>
          {openMenuId === record.uuid && record && menuPosition && (
            <Portal>
              <Box
                position="fixed"
                top={`${menuPosition.top}px`}
                right={`${menuPosition.right}px`}
                zIndex={1000}
                ref={menuRef}
              >
                <DrawerList>
                  <ActionDrawerItems title="" items={[
                    {
                      label: 'Abrir em nova aba',
                      variant: 'icon' as const,
                      icon: <Icon icon={ArrowSquareOutIcon} size={16} />,
                      onClick: () => {
                        setOpenMenuId(null);
                        setMenuPosition(null);
                        window.open(`/recorrencias/${record.uuid}`, '_blank');
                      }
                    }
                  ]} />
                </DrawerList>
              </Box>
            </Portal>
          )}
        </Box>
      ),
    },
  ];

  useOutsideClick({
    ref: menuRef,
    handler: () => {
      setOpenMenuId(null);
      setMenuPosition(null);
    },
  });

  useEffect(() => {
    const status = searchParams.get("status") || "ALL";
    const startDate = searchParams.get("startDate") || null;
    const endDate = searchParams.get("endDate") || null;
    const search = searchParams.get("search") || "";
    const searchType = searchParams.get("searchType") || "customerName";
    const page = Math.max(0, Number(searchParams.get("page") || 1) - 1);

    setSearchValue(search);
    setFilterParams(searchType);

    getSubscriptionsData({ status, startDate, endDate, search, searchType, page });
  }, [searchParams, pageSize]);

  return (
    <Box m="0">
      <PageHeading title="Recorrências" />
      <Flex align="center" gap="2" style={{ margin: "24px 0" }}>
        <Select
          fontSize="14px"
          w="130px"
          h="38px"
          borderRadius=".25rem"
          borderColor="#efefef"
          value={filterParams}
          onChange={(e) => setFilterParams(e.target.value)}
          color="black"
        >
          <option value="customerName">Nome</option>
          <option value="customerDocument">Documento</option>
        </Select>
        <Search
          placeholder="Buscar Recorrências"
          value={searchValue}
          onChange={setSearchValue}
          onSearch={handleSearch}
          expanded
        />
        <SubscriptionTableFilter
          onFilterChange={handleFilterChange}
          defaultValues={filterDefaultValues}
          appliedCount={appliedCount}
        />
      </Flex>

      <DynamicTable
        data={data?.content}
        columns={columns}
        rowKey={(record) => record.uuid}
        onRowClick={(record) => navigate(`/recorrencias/${record.uuid}`)}
        locale={{ emptyText: "Sem dados de recorrências" }}
        loading={loading}
        pagination={{
          currentPage: currentPage + 1,
          totalPages: data?.totalPages,
          onPageChange: handlePageChange,
          pageSize: pageSize,
          onPageSizeChange: handlePageSizeChange,
          totalItems: data?.totalElements,
        }}
      />

    </Box>
  );
};

export default WithAuth(Assinaturas);
